import { posthog, type PostHog, type JsonType, type PostHogConfig } from 'posthog-js';
import { defu } from 'defu';
import { env } from '@/utils/env'
import { onConsent } from '@/utils/cookieConsent'

export default defineNuxtPlugin({
  name: 'posthog',
  enforce: 'pre',
  setup: async () => {

    //  Only enable PostHog in production environment (app.myfindex.com)
    const isProduction = window.location.hostname === 'app.myfindex.com';

    if (!isProduction) {
      return {
        provide: {
          clientPosthog: null,
          posthog: 'Deprecated: use $clientPosthog instead.' as const,
        },
      };
    }

    const posthogFeatureFlags = useState<Record<string, boolean | string> | undefined>('ph-feature-flags');
    const posthogFeatureFlagPayloads = useState<Record<string, JsonType> | undefined>('ph-feature-flag-payloads');

    const state: { client: PostHog | null } = { client: null };

    onConsent('statistics', () => {
      const clientOptions = defu<PostHogConfig, Partial<PostHogConfig>[]>({}, {
        api_host: env.VITE_APP_POSTHOG_HOST,
        capture_pageview: false,
        capture_pageleave: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*'
        },
        bootstrap: {
          featureFlags: posthogFeatureFlags.value,
          featureFlagPayloads: posthogFeatureFlagPayloads.value,
        },
      });

      state.client = posthog.init(import.meta.env?.VITE_APP_POSTHOG_KEY || '', clientOptions) ?? null;

      const identity = useCookie('ph-identify');
      identity.value = posthog.get_distinct_id();

      // Make sure that pageviews are captured with each route change
      const router = useRouter();
      router.afterEach((to) => {
        posthog.capture('$pageview', {
          current_url: to.fullPath,
        });
      });

      posthog.onFeatureFlags((flags, featureFlags) => {
        posthogFeatureFlags.value = featureFlags;
        posthogFeatureFlagPayloads.value = flags.reduce<Record<string, JsonType>>((acc, flag) => {
          acc[flag] = posthog.getFeatureFlagPayload(flag);
          return acc;
        }, {});
      });
    });

    return {
      provide: {
        get clientPosthog(): PostHog | null { return state.client; },
        posthog: 'Deprecated: use $clientPosthog instead.' as const,
      },
    };
  },
});
